import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import uniqBy from 'lodash/uniqBy';

import {ChartType as CommonChartType, ProjectFeatureFlag} from 'common/report/constants';
import {BookmarkOverrides} from 'common/report/util/bookmark-overrides';
import {relativeDatesToFullLengthDateRangeRollingBettweenBookmark} from 'common/util/date-converters';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {BookmarkDateRange, DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Segfilter} from 'common/widgets/property-filter-menu/models/segfilter';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {PropertyType, ResourceType} from 'common/report/constants';
import {bookmarkFilterByCohortToCohortFilters, getFilterByCohorts} from 'common/widgets/query-block/filter-menu/util';
import {isEventSegment, isUserSegment, Segment} from 'common/widgets/query-block/segment-menu/types';
import {FiltersOperator} from 'common/widgets/query-entry-section/types';
import {
  BirthIntervalUnit,
  isRelativeRollingDateRange,
  RetentionBookmarkParams,
  RetentionChartType,
  RetentionType,
} from 'common/report/util/retention/types';
import {NON_INTERVALIZED_VALUE} from 'common/report/util/retention/types';
import {
  API_CHART_TYPE_TO_COMMON_CHART_TYPE,
  COMMON_CHART_TYPE_TO_API_CHART_TYPE,
} from 'common/report/util/retention/constants';

import {
  cohortsToFilterByCohortAndGroupBy,
  convertOnStringToSegment,
  getDataGroupName,
  getFlattenedSegmentType,
  getGroupByName,
  groupByToCohortsOrOn,
  isSegmented,
  isSegmentedByCohort,
  onToGroupBy,
} from './format';

// We need to pass context from both Retention (MPContext) report and Dashboards (DashboardsContext). They are
// different types, but they have common fields/methods we need access to from RetentionQuery
export type ContextInterface = {
  hasProjectFeatureFlag: (flag: ProjectFeatureFlag) => boolean;
  projectId: number;
};

// TODO: figure out if/how I want to use this.
export interface QueryInterface {
  retentionType: RetentionType;
  bornEvent: string;
  bornFilters: RetentionBookmarkParams['born_filters'];
  bornFiltersOp: FiltersOperator;
  bucketSizes: number[];
  chartType: RetentionChartType;
  dataGroupId: string;
  dateRange: BookmarkDateRange;
  dateUnit: BirthIntervalUnit;
  retentionUnit?: TimeUnit;
  cumulative: boolean;
  unbounded: boolean;
  percentageView: boolean;
  event: string;
  expandedSegments: string[];
  filters: RetentionBookmarkParams['filters'];
  filtersOp: FiltersOperator;
  filterByCohort: RetentionBookmarkParams['filter_by_cohort'];
  filterByEvent: RetentionBookmarkParams['filter_by_event'];
  groupBy: RetentionBookmarkParams['group_by'];
  segmentationEvent: string;
  retentionBucket: number;
  selectedSegments: string[];
  sortColumn: string;
  sortDesc: boolean;
  querySampling: boolean;
}

const DEFAULTS: QueryInterface = Object.freeze({
  bornEvent: null,
  event: null,
  dataGroupId: null,
  bornFilters: [],
  bornFiltersOp: FiltersOperator.And,
  bucketSizes: [],
  expandedSegments: [],
  filters: [],
  filtersOp: FiltersOperator.And,
  filterByCohort: {},
  filterByEvent: {},
  chartType: CommonChartType.RetentionTable,
  cumulative: true,
  dateUnit: TimeUnit.Day,
  retentionUnit: TimeUnit.Day,
  dateRange: {type: DateRangeType.RelativeAfter, from_date: {unit: TimeUnit.Day, value: 7}, to_date: `$now`},
  groupBy: [],
  percentageView: true,
  querySampling: false,
  retentionType: `birth`,
  segmentationEvent: null,
  selectedSegments: [],
  sortColumn: `cohort`,
  sortDesc: false,
  retentionBucket: 0,
  unbounded: true,
});

const UI_ONLY_FIELDS = [
  `expanded_segments`,
  `selected_segments`,
  `sort_desc`,
  `sort_column`,
  `percentage_view`,
  `chart_type`,
  `retention_bucket`,
] as const;
// asserts that each of UI_ONLY_FIELDS are assignable to type `keyof RetentionBookmarkParams` while
// preserving the `as const` nature of the original declaration. exported to avoid
// the "unused variable" tsc error.
export const _assertUiOnlyFieldsValid: ReadonlyArray<keyof RetentionBookmarkParams> = UI_ONLY_FIELDS;

export const ORDERED_TIME_UNITS: TimeUnit[] = [TimeUnit.Day, TimeUnit.Week, TimeUnit.Month];

/**
 * RetentionQuery represents the UI state, the information necessary to make a query,
 * and the logic to translate from URLs and any version of bookmark into the current UI
 * state.
 */
export class RetentionQuery implements QueryInterface {
  public readonly retentionType: RetentionType;
  public readonly bornEvent: string;
  public readonly bornFilters: Segfilter[];
  public readonly bornFiltersOp: FiltersOperator;
  public readonly bucketSizes: number[];
  public readonly chartType: RetentionChartType;
  public readonly dataGroupId: string;
  /** TODO: make dateRange an actual DateRange object */
  public readonly dateRange: BookmarkDateRange;
  public readonly dateUnit: BirthIntervalUnit;
  public readonly retentionUnit: TimeUnit;
  public readonly cumulative: boolean;
  public readonly unbounded: boolean;
  public readonly percentageView: boolean;
  public readonly event: string;
  public readonly filters: Segfilter[];
  public readonly filtersOp: FiltersOperator;
  public readonly filterByCohort: RetentionBookmarkParams['filter_by_cohort'];
  public readonly filterByEvent: RetentionBookmarkParams['filter_by_event'];
  public readonly groupBy: Segment[];
  public readonly segmentationEvent: string;

  public readonly selectedSegments: string[];
  public readonly expandedSegments: string[];
  public readonly sortDesc: boolean;
  public readonly sortColumn: string;
  public readonly retentionBucket: number;

  public querySampling: boolean;
  public context: ContextInterface;

  constructor(params: Partial<QueryInterface> = {}, {context}: {context: ContextInterface}) {
    this.context = context;

    let defaults = DEFAULTS;
    if (params.retentionType === `addiction`) {
      // The value of retentionUnit from DEFAULTS doesn't apply to addiction since addiction doesn't use retentionUnit
      defaults = {...defaults, retentionUnit: null};
    }

    Object.assign(this, defaults, getContextSensitiveDefaults(context), pick(params, Object.keys(DEFAULTS)));
  }

  createFromUpdate(params: Partial<QueryInterface>): RetentionQuery {
    return new RetentionQuery(
      {
        ...this,
        ...params,
      },
      {context: this.context},
    );
  }

  createFromChartTypeUpdate(chartType: RetentionChartType): RetentionQuery {
    const timeUnits: BirthIntervalUnit[] = ORDERED_TIME_UNITS;
    const params: Partial<QueryInterface> = {chartType};
    if (!this.isAddiction() && chartType === CommonChartType.RetentionTrend && !timeUnits.includes(this.dateUnit)) {
      // The `non_intervalized` unit isn't valid for trends view. If that's selected when switching to trends view,
      // we have to change the unit to something that's valid.
      params.dateUnit = ORDERED_TIME_UNITS[0];
    }

    if (!this.isAddiction() && chartType !== CommonChartType.RetentionTrend) {
      // In non-trends chart type, we only have `retentionUnit` in the UI. But we still maintain separate `dateUnit`
      // and `retentionUnit` state (this will make it easier to have these separated in the UI in the future).
      // Consequently, we have to make change one of them to match the other. We copy retentionUnit -> dateUnit
      // (rather than the other way around) because retention unit exists for both chart types, so it makes to keep
      // that the same when switching chard types.
      params.dateUnit = this.retentionUnit;
    }

    return this.createFromUpdate(params);
  }

  createFromDateUnitUpdate(dateUnit: BirthIntervalUnit): RetentionQuery {
    return this.createFromUpdate({
      dateUnit,
      // We only mirror dateUnit to retentionUnit if user is not whitelisted for new retention period picker. If they
      // are whitelisted, retentionUnit is updated explicitly when the retention unit selector changes.
      ...(!this.showTimeUpdates && !this.isAddiction() ? {retentionUnit: dateUnit as TimeUnit} : {}),
    });
  }

  createFromRetentionTypeUpdate(retentionType: RetentionType): RetentionQuery {
    return this.createFromUpdate({
      retentionType,
      ...(retentionType === `addiction` ? {retentionUnit: null} : {retentionUnit: this.dateUnit as TimeUnit}),
    });
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  static createFromBookmark(
    params: Partial<RetentionBookmarkParams>,
    {context}: {context: ContextInterface},
  ): RetentionQuery {
    const parsed: Partial<QueryInterface> = {};
    if (params.date_range) {
      if (isRelativeRollingDateRange(params.date_range)) {
        parsed.dateRange = relativeDatesToFullLengthDateRangeRollingBettweenBookmark({
          relativeFrom: Number(params.date_range.from),
          relativeTo: Number(params.date_range.to),
        });
      } else {
        parsed.dateRange = params.date_range;
      }
    }

    if (params.cohorts) {
      const parsedCohorts = cohortsToFilterByCohortAndGroupBy(params.cohorts, false);
      parsed.filterByCohort = parsedCohorts.filter_by_cohort;
      parsed.groupBy = parsedCohorts.group_by;
    } else {
      if (params.group_by) {
        parsed.groupBy = params.group_by;
      }

      if (params.filter_by_cohort) {
        parsed.filterByCohort = params.filter_by_cohort;
      }
    }

    if (params.filter_by_event) {
      parsed.filterByEvent = params.filter_by_event;
    }

    if (params.on) {
      parsed.groupBy = onToGroupBy(convertOnStringToSegment(params.on));
    }

    // Validate that (1) segmentationEvent corresponds to one of the events
    // (2) that it's not garbage. null and [] exist in the persisted bookmarks.
    if (params.segmentation_event && [params.born_event, params.event].includes(params.segmentation_event)) {
      parsed.segmentationEvent = params.segmentation_event;
    }

    if (params.retention_type === `compounded`) {
      // compounded retention is removed in favor of first time retention. 2020-03-11
      copy(params.event, parsed, `bornEvent`);
      copy(params.filters, parsed, `bornFilters`);
      copy(`birth`, parsed, `retentionType`);
    } else if (params.retention_type === `birth`) {
      copy(params.born_event, parsed, `bornEvent`);
      copy(params.born_filters, parsed, `bornFilters`);
      copy(params.born_filters_op, parsed, `bornFiltersOp`);
      copy(params.retention_type, parsed, `retentionType`);
    } else {
      copy(params.retention_type, parsed, `retentionType`);
    }

    // if unbounded retention is omit, default it to false
    if (params.retention_type === `compounded` || params.retention_type === `birth`) {
      parsed.unbounded = Boolean(params.unbounded_retention);
    }

    if (params.retention_type !== `addiction`) {
      if (params.retention_unit) {
        copy(params.retention_unit, parsed, `retentionUnit`);
        copy(params.bucket_sizes, parsed, `bucketSizes`);
      } else {
        // In the old format, `retention_unit` didn't exist on the bookmark. Convert bookmarks saved in the old
        // format to the new format where dateUnit and retentionUnit are split.
        copy(params.date_unit as TimeUnit, parsed, `retentionUnit`);
      }
    }

    parsed.chartType = API_CHART_TYPE_TO_COMMON_CHART_TYPE[params.chart_type];

    // No data translations to manage for these attributes.
    copy(params.event, parsed, `event`);
    copy(params.filters, parsed, `filters`);
    copy(params.filters_op, parsed, `filtersOp`);
    copy(params.date_unit, parsed, `dateUnit`);
    copy(params.percentage_view, parsed, `percentageView`);
    copy(params.selected_segments, parsed, `selectedSegments`);
    copy(params.expanded_segments, parsed, `expandedSegments`);
    copy(params.sort_column, parsed, `sortColumn`);
    copy(params.sort_desc, parsed, `sortDesc`);
    copy(params.cumulative, parsed, `cumulative`);
    copy(params.retention_bucket, parsed, `retentionBucket`);
    copy(params.data_group_id, parsed, `dataGroupId`);

    // Omit null, undefined and empty string so that the defaults can be filled in.
    return new RetentionQuery(omitNullAndUndefinedAndEmptyString(parsed), {context});
  }

  /**
   * Construct a serializable object which can reproduce the query and UI state.
   *
   * @param {boolean} [forQuery = false] if true, omit UI only fields
   * @param {boolean} [forUI = false] used for UI
   */
  generateBookmark({
    forQuery = false,
    forUI = false,
  }: {forQuery?: boolean; forUI?: boolean} = {}): RetentionBookmarkParams {
    /* eslint-disable camelcase */
    let bookmark: RetentionBookmarkParams = {
      date_unit: this.dateUnit,
      ...((this.showTimeUpdates || forUI) && !this.isAddiction()
        ? {
            retention_unit: this.retentionUnit,
            bucket_sizes: this.bucketSizes,
          }
        : {}),
      date_range: this.dateRange,
      chart_type: COMMON_CHART_TYPE_TO_API_CHART_TYPE[this.chartType],
      event: this.event,
      filters: this.filters,
      filters_op: this.filtersOp,
      born_event: this.bornEvent,
      born_filters: this.bornFilters,
      born_filters_op: this.bornFiltersOp,
      retention_type: this.retentionType,
      filter_by_cohort: this.filterByCohort,
      filter_by_event: this.filterByEvent,
      segmentation_event: this.segmentationEvent, // omit if empty?
      group_by: this.groupBy,
      data_group_id: this.dataGroupId,
      percentage_view: this.percentageView,
      cumulative: this.cumulative,
      unbounded_retention: this.unbounded,
      selected_segments: this.getSelectedSegments(),
      expanded_segments: this.getExpandedSegments(),
      sort_column: this.sortColumn,
      sort_desc: this.sortDesc,
      retention_bucket: this.retentionBucket,
    };
    /* eslint-enable camelcase */

    if (this.retentionType !== `birth`) {
      bookmark = omit(bookmark, [`born_event`, `born_filters`]);
    }

    if (forQuery) {
      bookmark = omit(bookmark, UI_ONLY_FIELDS);
    }

    return bookmark;
  }

  get showTimeUpdates() {
    return true;
  }

  getQueryType() {
    if (this.chartType === `retention-trend`) {
      return `retention_trends`;
    }

    return `retention_report`;
  }

  /** Test query equality, ignoring UI-only parameters */
  queryEquals(other: RetentionQuery): boolean {
    return (
      this.getQueryType() === other.getQueryType() &&
      this.querySampling === other.querySampling &&
      isEqual(this.generateBookmark({forQuery: true}), other.generateBookmark({forQuery: true}))
    );
  }

  /** Test equality of UI state and bookmark, for persistence */
  equals(other: RetentionQuery): boolean {
    return this.getQueryType() === other.getQueryType() && isEqual(this.generateBookmark(), other.generateBookmark());
  }

  // Begin convenience methods for the view

  shouldSyncDateAndRetentionUnits() {
    // In retention line/table, the retention unit and date unit have to the be same. In `addiction` mode, only the
    // `dateUnit` (and not `retentionUnit`) is used, so syncing isn't necessary.
    return !this.isAddiction() && this.chartType !== CommonChartType.RetentionTrend;
  }

  isNonIntervalized() {
    return this.dateUnit === NON_INTERVALIZED_VALUE;
  }

  getCohorts(dedupe = true) {
    const {cohorts} = groupByToCohortsOrOn(this.groupBy);

    if (dedupe) {
      return uniqBy(cohorts, (c) => c.id);
    }
    return cohorts;
  }

  isAddiction() {
    return this.retentionType === `addiction`;
  }

  /**
   * Checks if the group by is on a user property or cohorts.
   */
  isGroupByUser() {
    const group = this.groupBy ? this.groupBy[0] : null;
    return group && (Array.isArray(group) || Object.prototype.hasOwnProperty.call(group, `user`));
  }

  isSegmented() {
    return isSegmented(this.groupBy);
  }

  isSegmentedByCohort() {
    return isSegmentedByCohort(this.groupBy);
  }

  getGroupByName(group: Segment): string {
    return getGroupByName(group);
  }

  getSegmentName(): string {
    if (this.isSegmented() && !this.isSegmentedByCohort()) {
      return this.groupBy.map((group) => this.getGroupByName(group)).join(` / `);
      // TODO: revisit for B2B properties
    } else {
      return ``;
    }
  }

  getFlattenedSegmentType(): PropertyType {
    return getFlattenedSegmentType(this.groupBy);
  }

  isSegmentedByDataGroup(): boolean {
    return this.groupBy
      .map((group) => {
        if (isUserSegment(group)) {
          return group.user.dataGroupId !== undefined && group.user.dataGroupId !== null;
        } else if (isEventSegment(group)) {
          return group.event.dataGroupId !== undefined && group.event.dataGroupId !== null;
        }
        return false;
      })
      .reduce((acc, curr) => acc || curr, false);
  }

  getDataGroupName({plural}: {plural?: boolean} = {}): string {
    return getDataGroupName(this.dataGroupId, plural);
  }

  getSelectedSegments(): string[] {
    return this.selectedSegments;
  }

  getExpandedSegments(): string[] {
    return this.expandedSegments;
  }

  applyOverrides(overrides: BookmarkOverrides): RetentionQuery {
    if (isEmpty(overrides)) {
      return this;
    }

    const {additionalFilters, dateRangeOverride, trendUnitOverride} = overrides;
    const dateRange = dateRangeOverride ? {dateRange: new DateRange(dateRangeOverride).getBookmarkFormat()} : {};
    const dateUnit = trendUnitOverride ? {dateUnit: trendUnitOverride} : {};

    const additonalEventAndUserFilters = additionalFilters
      ? additionalFilters
          .filter((f) => [ResourceType.Event, ResourceType.User].includes(f.resourceType))
          .map((f) => new PropertyFilter(f))
      : [];
    const filterByEvent =
      additonalEventAndUserFilters.length > 0
        ? {
            filterByEvent: {
              operator: `and`,
              children: [
                ...((this.filterByEvent[`children`] as any[]) || []),
                ...additonalEventAndUserFilters.map((f) => ({[f.resourceType]: f})),
              ],
            },
          }
        : {};

    const additionalCohortFilters = additionalFilters
      ? additionalFilters.filter((f) => f.resourceType === ResourceType.Cohort).map((f) => new PropertyFilter(f))
      : [];
    const filterByCohort =
      additionalCohortFilters.length > 0
        ? {
            filterByCohort: getFilterByCohorts([
              ...bookmarkFilterByCohortToCohortFilters(this.filterByCohort),
              ...additionalCohortFilters,
            ]),
          }
        : {};

    return new RetentionQuery(
      {
        ...this,
        ...filterByCohort,
        ...filterByEvent,
        ...dateRange,
        ...dateUnit,
      },
      {
        context: this.context,
      },
    );
  }
}

function copy<T, K extends keyof T>(value: T[K], dest: T, destName: K) {
  if (typeof value === `boolean` || value) {
    dest[destName] = value;
  }
}

// eslint-disable-next-line @typescript-eslint/ban-types
function omitNullAndUndefinedAndEmptyString<T extends object>(obj: T): Partial<T> {
  return omitBy(obj, function filterEmptyValues(value, _key) {
    return value === undefined || value === null || ((value as unknown) as string) === ``;
  });
}

function getContextSensitiveDefaults(context: ContextInterface): Partial<QueryInterface> {
  const defaults: Partial<QueryInterface> = {};

  if (context === undefined || context === null) {
    return defaults;
  }

  if (context.hasProjectFeatureFlag(ProjectFeatureFlag.QTSByDefault)) {
    defaults.querySampling = true;
  }

  if (context.projectId === 2214121) {
    // For tinder POC, anything retention was too expensive for them.
    defaults.bornEvent = `app_open`;
    defaults.event = `app_open`;
  }

  return defaults;
}
